import React, { useState } from "react";
import { IEvidence } from "@interfaces/Interfaces";
import { IonSpinner } from "@ionic/react";
import { Icon_Tick_Blue } from "@assets/images";
import * as ProgramUtils from "@utils/programUtils";
import { format } from "date-fns";
import { UOC_PROGRAM_EVIDENCE_DEFINITION_IDS } from "@constants/programs";

const formatSentTime = (date: Date) => format(date, "dd MMMM yyyy 'at' HH:mm");

type SendState = "idle" | "sending" | "sent";

interface UOCEvidenceStatusBlockProps {
  evidence: IEvidence;
  onResend: () => Promise<void>;
  isCoolingDown: boolean;
  remindedAt: Date | null;
}

const formatDate = (date: string) => format(new Date(date), "dd MMM yyyy");

const RemindButton: React.FC<{ sendState: SendState; onResend: () => void; isCoolingDown: boolean }> = ({
  sendState,
  onResend,
  isCoolingDown,
}) => (
  <button
    onClick={onResend}
    disabled={sendState !== "idle" || isCoolingDown}
    className="ml-4 flex !min-h-[33px] min-w-[72px] flex-shrink-0 items-center justify-center rounded-[4px] bg-grey-20 px-3 py-1.5 text-acc-14px font-semibold text-cta-blue disabled:opacity-60"
  >
    {sendState === "sending" && <IonSpinner className="h-[18px] w-[18px] text-cta-blue" />}
    {sendState === "sent" && (
      <div className="flex flex-row items-center gap-1">
        <img
          src={Icon_Tick_Blue}
          className="h-[14px] w-[14px]"
          alt=""
        />
        <span>{"Sent"}</span>
      </div>
    )}
    {sendState === "idle" && <span>{"Remind PEd"}</span>}
  </button>
);

const bodyText = "text-13px font-semibold leading-default tracking-default text-grey-70";

const UOCEvidenceStatusBlock: React.FC<UOCEvidenceStatusBlockProps> = ({
  evidence,
  onResend,
  isCoolingDown,
  remindedAt,
}) => {
  const [sendState, setSendState] = useState<SendState>("idle");

  const programID = evidence.evidenceJSON?.programInfo?.programID ?? "";
  if (!ProgramUtils.isUOCApprenticeship(programID)) return null;

  const approvalState = evidence.evidenceJSON?.programInfo?.approvalState as string | undefined;
  if (!approvalState) return null;

  const expiryDate = evidence.evidenceJSON?.programInfo?.expiryDate as string | undefined;
  const isAsyncPending = approvalState === "Pending" && !!expiryDate;
  const isExpired = isAsyncPending && new Date(expiryDate!) < new Date();
  const isPending = isAsyncPending && !isExpired;
  const pedReviewDate = ProgramUtils.getUOCPEdReviewDate(evidence);

  const handleResend = async () => {
    if (sendState !== "idle") return;
    setSendState("sending");
    try {
      await onResend();
      setSendState("sent");
    } catch {
      setSendState("idle");
    }
  };

  if (isPending) {
    return (
      <div className="readEvidenceBlock !mt-0 mb-4">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="readEvidenceHeader !mb-0">{"This evidence is pending Practice Educator review"}</div>
            {evidence.createdAt && (
              <div
                className={bodyText}
              >{`The request link was sent to them on: ${formatDate(evidence.createdAt)}`}</div>
            )}
            {isCoolingDown && remindedAt && (
              <div
                className={bodyText}
              >{`Reminder sent on ${formatSentTime(remindedAt)}. Please wait 30 minutes before sending another reminder.`}</div>
            )}
          </div>
          <RemindButton
            sendState={sendState}
            onResend={handleResend}
            isCoolingDown={isCoolingDown}
          />
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="readEvidenceBlock !mt-0 mb-4">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="readEvidenceHeader !mb-0">
              {"This evidence has expired. The Practice Educator has not reviewed it."}
            </div>
            <div className={bodyText}>{`The request link sent to them expired on ${formatDate(expiryDate!)}`}</div>
            <div className={bodyText}>{"Click the 'Remind PEd' button to re-send a new request to the PEd"}</div>
            {isCoolingDown && remindedAt && (
              <div
                className={bodyText}
              >{`Reminder sent on ${formatSentTime(remindedAt)}. Please wait 30 minutes before sending another reminder.`}</div>
            )}
          </div>
          <RemindButton
            sendState={sendState}
            onResend={handleResend}
            isCoolingDown={isCoolingDown}
          />
        </div>
      </div>
    );
  }

  if (approvalState === "Approved") {
    const endOfYearReviewIDs = [
      UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_5_END_OF_YEAR_REVIEW,
      UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_6_END_OF_YEAR_REVIEW,
    ];
    const isEndOfYearReview = endOfYearReviewIDs.includes(
      evidence.evidenceJSON?.programInfo?.programEvidenceDefinitionID
    );

    if (isEndOfYearReview) {
      return (
        <div className="readEvidenceBlock !mt-0 mb-4">
          <div className="flex flex-col gap-1">
            <div className="readEvidenceHeader !mb-0">{"Your portfolio has been approved."}</div>
            <div className={bodyText}>{"It is now locked and waiting for academic review."}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="readEvidenceBlock !mt-0 mb-4">
        <div className="flex flex-col gap-1">
          <div className="readEvidenceHeader !mb-0">{"This evidence has been approved by the Practice Educator."}</div>
          <div className={bodyText}>{"It is now locked. You cannot edit it."}</div>
          {pedReviewDate && (
            <div className={bodyText}>{`Date of Practice Educator review: ${formatDate(pedReviewDate)}`}</div>
          )}
        </div>
      </div>
    );
  }

  if (approvalState === "Rejected") {
    const rejectionComment = evidence.evidenceJSON?.programInfo?.rejectionComment as string | undefined;
    return (
      <div className="readEvidenceBlock !mt-0 mb-4">
        <div className="flex flex-col gap-1">
          <div className="readEvidenceHeader !mb-0">
            {
              "This evidence has been rejected by the Practice Educator. This means that they do not think that they observed this."
            }
          </div>
          <div className={bodyText}>{"It is now locked. You cannot edit it."}</div>
          {pedReviewDate && (
            <div className={bodyText}>{`Date of Practice Educator review: ${formatDate(pedReviewDate)}`}</div>
          )}
          {rejectionComment && <div className={bodyText}>{`Rejection reason: ${rejectionComment}`}</div>}
        </div>
      </div>
    );
  }

  return null;
};

export default UOCEvidenceStatusBlock;
